import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Bug,
  Camera,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  ClipboardCheck,
  Clock3,
  Lightbulb,
  MessageSquareWarning,
  Search,
  Sparkles,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { LoadingState } from "@/components/data/LoadingState";
import { EmptyState } from "@/components/data/EmptyState";
import { ErrorState } from "@/components/data/ErrorState";
import { JsonViewer } from "@/components/data/JsonViewer";
import { RelTime } from "@/components/data/RelTime";
import { StatusBadge } from "@/components/data/StatusBadge";
import { api, API_BASE } from "@/lib/api";
import { qk, useFeedback, useFeedbackDetail, paginatedItems } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { fmtBytes, fmtDateTime, truncate } from "@/lib/format";
import type { ActivityRow, ErrorRow, FeedbackPriority, FeedbackRow, FeedbackStatus } from "@/types";

const PAGE = 30;

const STATUSES: Array<{ value: FeedbackStatus | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "triaged", label: "Triaged" },
  { value: "planned", label: "Planned" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const KINDS = [
  { value: "all", label: "All" },
  { value: "bug", label: "Bugs" },
  { value: "feature", label: "Features" },
  { value: "confusing", label: "Confusing" },
  { value: "question", label: "Questions" },
  { value: "praise", label: "Praise" },
  { value: "other", label: "Other" },
];

const PRIORITIES: Array<{ value: FeedbackPriority | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "urgent", label: "Urgent" },
  { value: "high", label: "High" },
  { value: "normal", label: "Normal" },
  { value: "low", label: "Low" },
];

function isTruthy(value: unknown): boolean {
  return value === true || value === 1 || value === "1";
}

function kindIcon(kind: string) {
  switch (kind) {
    case "bug": return <Bug className="h-3.5 w-3.5" />;
    case "feature": return <Lightbulb className="h-3.5 w-3.5" />;
    case "confusing": return <AlertTriangle className="h-3.5 w-3.5" />;
    case "praise": return <Sparkles className="h-3.5 w-3.5" />;
    default: return <MessageSquareWarning className="h-3.5 w-3.5" />;
  }
}

function kindVariant(kind: string): BadgeProps["variant"] {
  switch (kind) {
    case "bug": return "error";
    case "feature": return "info";
    case "confusing": return "warn";
    case "praise": return "success";
    default: return "outline";
  }
}

function priorityVariant(priority: FeedbackPriority): BadgeProps["variant"] {
  switch (priority) {
    case "urgent": return "error";
    case "high": return "warn";
    case "low": return "neutral";
    default: return "outline";
  }
}

function statusVariant(status: FeedbackStatus): BadgeProps["variant"] {
  switch (status) {
    case "resolved":
    case "closed":
      return "success";
    case "in_progress":
      return "info";
    case "planned":
    case "triaged":
      return "warn";
    default:
      return "outline";
  }
}

function imageUrl(id: number): string {
  return `${API_BASE}/api/admin/feedback/${id}/image`;
}

export function FeedbackPage() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(params.get("status") ?? "open");
  const [kind, setKind] = useState(params.get("kind") ?? "all");
  const [priority, setPriority] = useState(params.get("priority") ?? "all");
  // The search box is a draft until Apply (or Enter); the query only sees the
  // applied value, and every filter change goes back to page 1.
  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const [search, setSearch] = useState(searchInput);
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const raw = params.get("id");
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  });

  const queryParams = useMemo(
    () => ({
      limit: PAGE,
      offset: page * PAGE,
      status: status === "all" ? undefined : status,
      kind: kind === "all" ? undefined : kind,
      priority: priority === "all" ? undefined : priority,
      search: search.trim() || undefined,
    }),
    [status, kind, priority, search, page]
  );

  const list = useFeedback(queryParams, { refetchInterval: 15_000 });
  const rows = paginatedItems<FeedbackRow>(list.data);
  const total = list.data?.total ?? rows.length;
  const detail = useFeedbackDetail(selectedId);

  useEffect(() => {
    if (selectedId == null && rows[0]) {
      setSelectedId(rows[0].id);
    }
  }, [rows, selectedId]);

  const apply = (overrides: { status?: string; kind?: string; priority?: string } = {}) => {
    const nextStatus = overrides.status ?? status;
    const nextKind = overrides.kind ?? kind;
    const nextPriority = overrides.priority ?? priority;
    const next = new URLSearchParams();
    if (nextStatus !== "open") next.set("status", nextStatus);
    if (nextKind !== "all") next.set("kind", nextKind);
    if (nextPriority !== "all") next.set("priority", nextPriority);
    if (searchInput.trim()) next.set("search", searchInput.trim());
    if (selectedId != null) next.set("id", String(selectedId));
    setParams(next, { replace: true });
    setStatus(nextStatus);
    setKind(nextKind);
    setPriority(nextPriority);
    setSearch(searchInput);
    setPage(0);
  };

  const selectRow = (id: number) => {
    setSelectedId(id);
    const next = new URLSearchParams(params);
    next.set("id", String(id));
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader
        eyebrow="private beta"
        title="Feedback"
        description="Bug reports, feature requests, screenshots, and device context from the app."
        actions={
          <Badge variant={list.isFetching ? "info" : "outline"} className="gap-1.5">
            <span className={cn("h-1.5 w-1.5 rounded-full", list.isFetching ? "animate-pulse bg-sky-400" : "bg-emerald-400")} />
            {list.isFetching ? "fetching" : "live · 15s"}
          </Badge>
        }
      />

      <section className="mb-4 grid gap-3 md:grid-cols-3">
        <Kpi label="Open reports" value={list.data?.summary.open ?? 0} icon={<CircleDot className="h-4 w-4" />} />
        <Kpi label="Urgent" value={list.data?.summary.urgent ?? 0} icon={<AlertTriangle className="h-4 w-4" />} tone="error" />
        <Kpi label="Open bugs" value={list.data?.summary.bugs ?? 0} icon={<Bug className="h-4 w-4" />} tone="warn" />
      </section>

      <Card className="mb-4 p-3">
        <div className="grid gap-3 lg:grid-cols-[1.5fr,1fr,1fr,1fr,auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              aria-label="Search feedback"
              placeholder="Search message, title, user"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-8"
              onKeyDown={(e) => {
                if (e.key === "Enter") apply();
              }}
            />
          </div>
          <Select value={status} onValueChange={(v) => apply({ status: v })}>
            <SelectTrigger aria-label="Status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={kind} onValueChange={(v) => apply({ kind: v })}>
            <SelectTrigger aria-label="Kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {KINDS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={priority} onValueChange={(v) => apply({ priority: v })}>
            <SelectTrigger aria-label="Priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => apply()}>Apply</Button>
        </div>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[minmax(360px,0.9fr),minmax(0,1.5fr)]">
        <Card className="overflow-hidden">
          <CardHeader className="border-b border-border/40 py-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle>Inbox</CardTitle>
              <span className="text-xs tabular-nums text-muted-foreground">{total.toLocaleString()} total</span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[calc(100vh-360px)] overflow-y-auto scrollbar-thin">
              {list.isLoading ? (
                <LoadingState />
              ) : list.isError ? (
                <ErrorState error={list.error} onRetry={() => list.refetch()} />
              ) : rows.length === 0 ? (
                <EmptyState title="No feedback" description="Try a wider filter." />
              ) : (
                <ul className="divide-y divide-border/40">
                  {rows.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        onClick={() => selectRow(row.id)}
                        className={cn(
                          "w-full px-4 py-3 text-left transition-colors hover:bg-secondary/40",
                          selectedId === row.id && "bg-secondary/60"
                        )}
                      >
                        <FeedbackSummary row={row} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-border/40 px-3 py-2 text-xs text-muted-foreground">
              <span className="tabular-nums">
                {total === 0 ? "0 rows" : `${page * PAGE + 1}-${Math.min((page + 1) * PAGE, total)} of ${total.toLocaleString()}`}
              </span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </Button>
                <span className="px-2 tabular-nums">page {page + 1}</span>
                <Button size="sm" variant="ghost" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <FeedbackDetailPanel
          selectedId={selectedId}
          row={detail.data?.feedback ?? null}
          activity={detail.data?.context.activity ?? []}
          errors={detail.data?.context.errors ?? []}
          loading={detail.isLoading}
          error={detail.error}
          onRetry={() => detail.refetch()}
        />
      </div>
    </>
  );
}

function Kpi({ label, value, icon, tone = "default" }: { label: string; value: number; icon: React.ReactNode; tone?: "default" | "warn" | "error" }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="mt-1 text-2xl font-semibold tabular-nums">{value.toLocaleString()}</div>
        </div>
        <div className={cn(
          "grid h-9 w-9 place-items-center rounded-md",
          tone === "error" ? "bg-rose-500/10 text-rose-300" : tone === "warn" ? "bg-amber-500/10 text-amber-300" : "bg-sky-500/10 text-sky-300"
        )}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

function FeedbackSummary({ row }: { row: FeedbackRow }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs">
        <Badge variant={kindVariant(row.kind)} className="capitalize">{kindIcon(row.kind)} {row.kind}</Badge>
        <Badge variant={priorityVariant(row.priority)} className="capitalize">{row.priority}</Badge>
        {isTruthy(row.has_image) && <Camera className="h-3.5 w-3.5 text-sky-300" />}
        <span className="ml-auto text-muted-foreground"><RelTime value={row.created_at} /></span>
      </div>
      <div>
        <p className="line-clamp-1 text-sm font-medium">{row.title || truncate(row.message, 80)}</p>
        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{row.message}</p>
      </div>
      <div className="flex min-w-0 items-center gap-2 text-[11px] text-muted-foreground">
        <Badge variant={statusVariant(row.status)} className="capitalize">{row.status.replace("_", " ")}</Badge>
        <span className="truncate">{row.screen ?? row.trigger}</span>
        {row.nearby_errors ? <span className="text-rose-300">{row.nearby_errors} nearby errors</span> : null}
      </div>
    </div>
  );
}

function FeedbackDetailPanel({
  selectedId,
  row,
  activity,
  errors,
  loading,
  error,
  onRetry,
}: {
  selectedId: number | null;
  row: FeedbackRow | null;
  activity: ActivityRow[];
  errors: ErrorRow[];
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState("");

  useEffect(() => {
    setNotes(row?.admin_notes ?? "");
  }, [row?.id, row?.admin_notes]);

  const update = useMutation({
    mutationFn: async (body: Partial<Pick<FeedbackRow, "status" | "priority" | "admin_notes" | "assigned_to">>) => {
      if (!row) throw new Error("No feedback selected");
      return api.patch<FeedbackRow>(`/api/admin/feedback/${row.id}`, body);
    },
    onSuccess: async () => {
      toast.success("Feedback updated");
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["feedback"] }),
        row ? qc.invalidateQueries({ queryKey: qk.feedbackDetail(row.id) }) : Promise.resolve(),
      ]);
    },
    onError: (err) => toast.error("Update failed", { description: err instanceof Error ? err.message : "Unknown error" }),
  });

  if (selectedId == null) {
    return (
      <Card>
        <EmptyState title="Select feedback" description="Open a report to inspect screenshots, debug data, and nearby events." />
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <LoadingState label="Loading feedback..." />
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <ErrorState error={error} onRetry={onRetry} />
      </Card>
    );
  }

  if (!row) return null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge variant={kindVariant(row.kind)} className="capitalize">{kindIcon(row.kind)} {row.kind}</Badge>
                <Badge variant={statusVariant(row.status)} className="capitalize">{row.status.replace("_", " ")}</Badge>
                <Badge variant={priorityVariant(row.priority)} className="capitalize">{row.priority}</Badge>
              </div>
              <h2 className="text-lg font-semibold tracking-tight">{row.title || truncate(row.message, 120)}</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                #{row.id} · {fmtDateTime(row.created_at)} · {row.trigger.replaceAll("_", " ")}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={row.status} onValueChange={(statusValue) => update.mutate({ status: statusValue as FeedbackStatus })}>
                <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.filter((s) => s.value !== "all").map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={row.priority} onValueChange={(priorityValue) => update.mutate({ priority: priorityValue as FeedbackPriority })}>
                <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.filter((s) => s.value !== "all").map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-border/50 bg-background/40 p-4 text-sm leading-relaxed whitespace-pre-wrap">
            {row.message}
          </div>

          {isTruthy(row.has_image) ? (
            <div className="overflow-hidden rounded-md border border-border/60 bg-background/40">
              <div className="flex items-center justify-between gap-2 border-b border-border/40 px-3 py-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-2"><Camera className="h-3.5 w-3.5" /> Screenshot / attachment</span>
                <span>{row.image_mime_type ?? "image"} · {fmtBytes(row.image_size)}</span>
              </div>
              <a href={imageUrl(row.id)} target="_blank" rel="noreferrer" className="block bg-black/20">
                <img src={imageUrl(row.id)} alt={`Feedback ${row.id} attachment`} className="max-h-[520px] w-full object-contain" />
              </a>
            </div>
          ) : null}

          <div className="grid gap-3 lg:grid-cols-2">
            <Fact icon={<UserRound className="h-4 w-4" />} label="User">
              <Link to={`/users/${row.user_id}`} className="font-mono text-sky-300 hover:underline">
                {row.user_email ?? row.user_id}
              </Link>
            </Fact>
            <Fact icon={<ClipboardCheck className="h-4 w-4" />} label="Contact">
              {isTruthy(row.contact_ok) ? "User is ok with follow-up" : "No follow-up requested"}
            </Fact>
            <Fact icon={<Clock3 className="h-4 w-4" />} label="Screen">{row.screen ?? "unknown"}</Fact>
            <Fact icon={<CheckCircle2 className="h-4 w-4" />} label="Build">
              {[row.platform, row.app_version, row.app_build].filter(Boolean).join(" / ") || "unknown"}
            </Fact>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Device context</div>
              <JsonViewer
                value={{
                  os_version: row.os_version,
                  device_model: row.device_model,
                  locale: row.locale,
                  timezone: row.timezone,
                  app_state: row.app_state,
                }}
              />
            </div>
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Debug payload</div>
              <JsonViewer value={row.debug ?? null} />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Admin notes</div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Decision, reproduction notes, Linear/GitHub link..." className="min-h-24" />
            <div className="flex justify-end">
              <Button size="sm" disabled={update.isPending || notes === (row.admin_notes ?? "")} onClick={() => update.mutate({ admin_notes: notes || null })}>
                Save notes
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <ContextCard title="Nearby activity" rows={activity} empty="No nearby requests">
          {(item) => <ActivityItem key={item.id} row={item} />}
        </ContextCard>
        <ContextCard title="Nearby errors" rows={errors} empty="No nearby errors">
          {(item) => <ErrorItem key={item.id} row={item} />}
        </ContextCard>
      </div>
    </div>
  );
}

function Fact({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border/50 bg-background/40 p-3">
      <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function ContextCard<T>({ title, rows, empty, children }: { title: string; rows: T[]; empty: string; children: (row: T) => React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">{empty}</p>
        ) : (
          <ul className="divide-y divide-border/40">{rows.slice(0, 8).map(children)}</ul>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityItem({ row }: { row: ActivityRow }) {
  return (
    <li className="py-2 text-xs">
      <div className="flex items-center gap-2">
        <StatusBadge status={row.status} />
        <span className="font-mono">{row.method}</span>
        <span className="truncate font-mono text-muted-foreground">{row.path}</span>
        <span className="ml-auto text-muted-foreground"><RelTime value={row.created_at} /></span>
      </div>
      <div className="mt-1 text-muted-foreground">{row.duration_ms ?? 0} ms · {truncate(row.user_agent ?? "", 90)}</div>
    </li>
  );
}

function ErrorItem({ row }: { row: ErrorRow }) {
  return (
    <li className="py-2 text-xs">
      <div className="flex items-center gap-2">
        <StatusBadge status={row.status} />
        <span className="truncate font-mono text-rose-200/90">{row.path ?? "unknown path"}</span>
        <span className="ml-auto text-muted-foreground"><RelTime value={row.created_at} /></span>
      </div>
      <p className="mt-1 line-clamp-2 text-muted-foreground">{row.message}</p>
    </li>
  );
}
